import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Collapse, InputAdornment, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CellphoneIcon from "@iconify/icons-mdi/cellphone";
import EmailIcon from "@iconify/icons-mdi/email";
import LinkVariantIcon from "@iconify/icons-mdi/link-variant";
import { InlineIcon } from "@iconify/react";
import { FormikProvider, useFormik } from "formik";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcFormSwitch,
  OcTextFieldFormik,
} from "oc/components";
import { useOcProperty } from "oc/hooks";
import { getPhoneFragment, useOcFormikHelper } from "oc/utils";
import * as Yup from "yup";

interface MyHrSystemSettingsHelpdeskCardProps {
  isMobile: boolean;
}

export const MyHrSystemSettingsHelpdeskCard = ({
  isMobile,
}: MyHrSystemSettingsHelpdeskCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [checked, setChecked] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { data: helpdeskEmail, loading: helpdeskEmailLoading } = useOcProperty({
    resource: "system",
    key: "helpdeskEmail",
  });

  const { data: helpdeskPhone, loading: helpdeskPhoneLoading } = useOcProperty({
    resource: "system",
    key: "helpdeskPhone",
  });

  const { data: helpdeskURL, loading: helpdeskURLoading } = useOcProperty({
    resource: "system",
    key: "helpdeskURL",
  });

  const { data: helpdeskCustomText, loading: helpdeskCustomTextLoading } =
    useOcProperty({
      resource: "system",
      key: "helpdeskCustomText",
    });

  const isPropsLoading =
    helpdeskEmailLoading ||
    helpdeskPhoneLoading ||
    helpdeskURLoading ||
    helpdeskCustomTextLoading;

  const initialValues = useMemo(
    () => ({
      email: helpdeskEmail ?? "",
      url: helpdeskURL ?? "",
      phone: helpdeskPhone ?? "",
      customText: helpdeskCustomText ?? "",
    }),
    [helpdeskEmail, helpdeskURL, helpdeskPhone, helpdeskCustomText]
  );

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      email: Yup.string()
        .matches(
          /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/,
          t("MY_HR_ERROR_MESSAGE_WRONG_EMAIL_FORMAT")
        )
        .email(t("MY_HR_ERROR_MESSAGE_WRONG_EMAIL_FORMAT")),
      url: Yup.string(),
      phone: Yup.string(),
      customText: Yup.string(),
    }),
    onSubmit: () => {
      handleSave();
    },
    validateOnChange: false,
    enableReinitialize: true,
  });

  const { loadingState, updateProperties } = useOcFormikHelper({
    formik,
    defaultResource: "system",
    valuesExtraData: {
      customText: {
        key: "helpdeskCustomText",
        loading: helpdeskCustomTextLoading,
        type: "input",
      },
      email: {
        key: "helpdeskEmail",
        loading: helpdeskEmailLoading,
        type: "input",
      },
      phone: {
        key: "helpdeskPhone",
        loading: helpdeskPhoneLoading,
        type: "input",
      },
      url: {
        key: "helpdeskURL",
        loading: helpdeskURLoading,
        type: "input",
      },
    },
  });

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value = event.target.value.replaceAll(/\s/g, "");
    const newValue = event.target.value;
    if (/^\d*$/.test(newValue)) {
      const format = newValue[0] === "1" ? [1, 5] : [2, 6];
      const maxLength = newValue[0] === "1" ? 8 : 9;

      getPhoneFragment(
        event,
        (e) => formik.setFieldValue("phone", (e.target as HTMLInputElement).value),
        "helpdeskPhone",
        maxLength,
        format
      );
    }
  };

  const handleSave = () => {
    updateProperties();
    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    formik.resetForm();
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleDialogClose = () => {
    setShowConfirmDialog(false);
    formik.submitForm();
  };

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleDialogClose}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />

      <OcCard
        label={t("MY_HR_HELPDESK_CONTACTS")}
        loading={loadingState || isPropsLoading}
      >
        {!isPropsLoading && (
          <FormikProvider value={formik}>
            <Box
              display="flex"
              flexDirection="column"
              padding={isMobile ? "16px 0px" : "16px 32px"}
            >
              <OcTextFieldFormik
                name="email"
                id="email-input"
                disabled={loadingState}
                label={t("MY_HR_EMAIL")}
                sx={{ width: isMobile ? "auto" : 432 }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <InlineIcon
                          height="24px"
                          icon={EmailIcon}
                          color={theme.palette.gray60.main}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Box height="32px" />
              <OcTextFieldFormik
                name="url"
                id="url-input"
                label={t("MY_HR_HELPDESK_URL")}
                variant="outlined"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <InlineIcon
                          height="24px"
                          icon={LinkVariantIcon}
                          color={theme.palette.gray60.main}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
                style={{ width: isMobile ? "auto" : 432 }}
                disabled={loadingState}
              />
              <Box height="36px" />
              <Box
                style={{
                  width: isMobile ? "auto" : "432px",
                  borderBottom: "1px solid #C6CACC",
                }}
              />
              <Box height="36px" />
              <OcTextFieldFormik
                name="phone"
                id="phone-input"
                label={t("MY_HR_PHONE")}
                helperText={t("OC_OPTIONAL")}
                onChange={handlePhoneChange}
                style={{ width: isMobile ? "auto" : 432 }}
                disabled={loadingState}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <InlineIcon
                          height="24px"
                          icon={CellphoneIcon}
                          color={theme.palette.gray60.main}
                        />
                      </InputAdornment>
                    ),
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography
                          variant="body1"
                          color={theme.palette.gray60.main}
                        >
                          +36
                        </Typography>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Box height="36px" />
              <Box
                style={{
                  width: isMobile ? "auto" : "432px",
                  borderBottom: "1px solid #C6CACC",
                }}
              />
              <Box height="36px" />
              <Box display="flex" flexDirection="row" alignItems="center">
                <OcFormSwitch
                  id="customHelpdeskText"
                  checked={checked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setChecked(e.target.checked)
                  }
                  inputProps={{ "aria-label": "controlled" }}
                  disabled={loadingState}
                />
                <Box width="16px" />
                <Typography variant="body1">
                  {t("MY_HR_HELPDESK_CUSTOM_INFO_TEXT")}
                </Typography>
              </Box>
              <Collapse in={checked} timeout="auto">
                <Box height="36px" />
                <OcTextFieldFormik
                  name="customText"
                  id="custom-text-input"
                  multiline
                  label={t("MY_HR_CUSTOM_TEXT")}
                  slotProps={{
                    formHelperText: {
                      sx: {
                        textAlign: formik.values.customText ? "right" : "left",
                      },
                    },
                    htmlInput: {
                      maxLength: 255,
                    },
                  }}
                  disabled={loadingState}
                  style={{ width: isMobile ? "100%" : 432, height: "auto" }}
                  helperText={
                    !formik.values.customText
                      ? t("OC_OPTIONAL")
                      : `${formik.values.customText.length}/255`
                  }
                />
              </Collapse>
            </Box>
            <OcCardButtonBar
              handleRightButton={handleCancel}
              rightButtonColor="secondary"
              rightButtonText={t("OC_CANCEL")}
              rightButtonId="cancel-helpdesk-settings"
              handlePrimaryButton={() => setShowConfirmDialog(true)}
              primaryButtonText={t("OC_SAVE")}
              primaryButtonDisabled={loadingState || !formik.dirty}
              primaryButtonId="save-helpdesk-settings"
            />
          </FormikProvider>
        )}
      </OcCard>
    </>
  );
};

export default MyHrSystemSettingsHelpdeskCard;
